import React, { useEffect, useState } from "react";
import {
  Modal,
  Stack,
  Group,
  Text,
  MultiSelect,
  Button,
  ActionIcon,
  Divider,
  Tooltip,
  Box,
  ScrollArea,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { IconUserPlus, IconUserMinus } from "@tabler/icons-react";
import { Employee } from "../../types/salon";
import { StaffTier } from "../../types/staffTier";
import { staffLabel } from "./StaffTierMembersCell";

interface StaffTierAddRemoveModalProps {
  opened: boolean;
  onClose: () => void;
  tier: StaffTier;
  members: Employee[];
  assignable: Employee[];
  busy?: boolean;
  /** Assign the chosen staff to this tier (the container then offers to sync). */
  onAdd: (staffIds: string[]) => Promise<void>;
  /** Unparent a member (leaves their services/commissions untouched). */
  onRemove: (staffId: string) => Promise<void>;
}

/**
 * Add staff to a tier or remove them, from the tier's kebab menu. Adding routes
 * the just-added staff through the standardized sync prompt (handled by the
 * container); removing is a plain unparent — no sync, nothing overwritten.
 */
const StaffTierAddRemoveModal: React.FC<StaffTierAddRemoveModalProps> = ({
  opened,
  onClose,
  tier,
  members,
  assignable,
  busy = false,
  onAdd,
  onRemove,
}) => {
  const isMobile = useMediaQuery("(max-width: 48em)");
  const [selected, setSelected] = useState<string[]>([]);

  useEffect(() => {
    if (opened) setSelected([]);
  }, [opened]);

  const handleAdd = async (): Promise<void> => {
    if (selected.length === 0) return;
    const ids = selected;
    setSelected([]);
    await onAdd(ids);
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={`Add or remove staff — ${tier.name}`}
      centered
      size="lg"
      fullScreen={isMobile}
    >
      <Stack>
        <Group align="flex-end" gap="sm" wrap="nowrap">
          <MultiSelect
            label="Add staff to this tier"
            placeholder={
              assignable.length ? "Select staff" : "No staff available to add"
            }
            data={assignable.map((e) => ({
              value: e.id,
              label: staffLabel(e),
            }))}
            value={selected}
            onChange={setSelected}
            searchable
            disabled={!assignable.length || busy}
            style={{ flex: 1 }}
          />
          <Button
            leftSection={<IconUserPlus size={16} />}
            color="var(--brand-teal)"
            onClick={handleAdd}
            loading={busy}
            disabled={selected.length === 0}
          >
            Add
          </Button>
        </Group>

        <Divider label={`${members.length} assigned`} labelPosition="left" />

        {members.length === 0 ? (
          <Text c="dimmed" size="sm">
            No staff are assigned to this tier yet.
          </Text>
        ) : (
          <ScrollArea.Autosize mah={320}>
            <Stack gap="xs">
              {members.map((member) => (
                <Group key={member.id} justify="space-between" wrap="nowrap">
                  <Text size="sm">{staffLabel(member)}</Text>
                  <Tooltip label="Remove from tier (keeps their services & commissions)">
                    <Box>
                      <ActionIcon
                        variant="subtle"
                        color="red"
                        aria-label={`Remove ${staffLabel(member)}`}
                        disabled={busy}
                        onClick={() => onRemove(member.id)}
                      >
                        <IconUserMinus size={16} />
                      </ActionIcon>
                    </Box>
                  </Tooltip>
                </Group>
              ))}
            </Stack>
          </ScrollArea.Autosize>
        )}
      </Stack>
    </Modal>
  );
};

export default StaffTierAddRemoveModal;
